import {FC, useMemo, useState} from 'react';
import {useCan, useCanAny, usePersona} from 'src/permissions/hooks';
import {GridColumns, GridSortItem, GridSortModel} from '@mui/x-data-grid';
import {Box, Typography} from '@mui/material';
import {Add} from '@mui/icons-material';
import dayjs from 'dayjs';
import {FormattedMessage, useIntl} from 'react-intl';
import {Link} from 'react-router-dom';
import {Table} from '../../tables/Table';
import AMPModal from '../AMPModal';
import {CreateRfidForm} from '../../forms/CreateRfidForm';
import {RfidActions} from './RfidActions';
import {BlockStatusEnum, RfidTableProps} from './RfidTable.types';
import {getObjectFieldAsString} from '../../../utils/helper';
import {rfidTableSx} from './RfidTable.sx';
import {RfIdItemCard} from './RfidItemCard';
import {TableLayout} from 'src/components/tables/TableLayout';
import {ResponsiveTable} from '../ResponsiveTable';
import {CardList} from 'src/components/common/CardList';
import {RequestRfid} from '../RequestRfid';
import {EmployeeCell} from '../../users/EmployeeCell';

export const RfidTable: FC<RfidTableProps> = ({data, isCompanyUser = false, companyId, onRefetchRfidData}) => {
    const canManageRfid = useCanAny('rfidTags.update', 'rfidTags.create', 'rfidTags.delete');
    const intl = useIntl();
    const persona = usePersona();
    const [sortValue, setSortValue] = useState<GridSortItem>({field: '', sort: 'desc'});
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showRequestModal, setShowRequestModal] = useState(false);

    const isEmployeeUser = persona.isEmployee;
    // A driver asks the business for a tag; everyone else with the permission creates one.
    const requestsRatherThanCreates = persona.isDriver;
    const canAddRfid = useCan('rfidTags.create');

    const actionsCol: GridColumns =
        canManageRfid
            ? [
                {
                    field: 'actions',
                    headerName: '',
                    width: 120,
                    sortable: false,
                    cellClassName: 'ampTableCell',
                    align: 'right' as const,
                    headerAlign: 'right' as const,
                    renderCell: (params) => {
                        return (
                            <>
                                <RfidActions
                                    businessId={data?.business?.id}
                                    blocked={params?.row?.blocked}
                                    ocppTagId={params?.row?.id}
                                    idTag={params?.row?.idTag}
                                    licensePlate={params?.row?.licensePlate}
                                    tagCompanyId={params?.row?.company?.id}
                                    tagEmployeeId={params?.row?.employee?.id}
                                    tagUserId={params?.row?.users?.[0]?.id}
                                />
                            </>
                        );
                    },
                },
            ]
            : [];

    const employeeColumn: GridColumns = isCompanyUser ? [{
        field: 'employee.firstName',
        renderHeader: () => (
            <Typography sx={rfidTableSx.colTitle}>
                <FormattedMessage id={'employee'}/>
            </Typography>
        ),
        flex: 1,
        sortable: false,
        renderCell: (params) => {
            const rowData = params?.row;
            if (rowData?.employee?.firstName && rowData?.employee?.lastName) {
                const employeeData = {
                    ...rowData.employee,
                    bexioDataList: rowData.employee?.user?.bexioDataList,
                    employee: {
                        ...rowData.employee,
                        companyId: rowData?.company?.id ?? rowData?.employee?.company?.id ?? 0
                    },
                    business: {
                        id: rowData?.business?.id ?? 0
                    }
                };
                return <EmployeeCell data={employeeData as any} isEmployeeUser={isEmployeeUser}/>;
            }
            return <Typography color="primary.dark">-</Typography>;
        },
    }] : [];

    const columns: GridColumns = [
        {
            field: 'idTag',
            renderHeader: () => (
                <Typography sx={rfidTableSx.colTitle}>
                    <FormattedMessage id={'rfidTag'}/>
                </Typography>
            ),
            flex: 1,
            sortable: true,
            // The tag number opens the tag, as it does in the tags list's own table. This list had
            // no way into a tag at any width - the number was plain text here and in the card.
            renderCell: (params) => (
                <Link to={`/app/rfids/${params?.row?.id}`}>
                    <Typography color="primary.dark">{params?.row?.idTag ?? ''}</Typography>
                </Link>
            ),
        },
        ...employeeColumn,
        {
            field: 'licensePlate',
            renderHeader: () => (
                <Typography sx={rfidTableSx.colTitle}>
                    <FormattedMessage id={'licensePlate'}/>
                </Typography>
            ),
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <Typography>{params?.row?.licensePlate || '—'}</Typography>
            ),
        },
        {
            field: 'blocked',
            renderHeader: () => (
                <Typography sx={rfidTableSx.colTitle}>
                    <FormattedMessage id={'statusColumnHeader'}/>
                </Typography>
            ),
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Box
                    sx={[
                        rfidTableSx.tag,
                        !params?.row?.blocked && rfidTableSx.tagActive,
                        params?.row?.blocked && rfidTableSx.tagBlocked,
                    ]}
                >
                    <FormattedMessage
                        id={params.row?.blocked ? BlockStatusEnum.blocked : BlockStatusEnum.accepted}
                    />
                </Box>
            ),
        },
        {
            field: 'created',
            renderHeader: () => (
                <Typography sx={rfidTableSx.colTitle}>
                    <FormattedMessage id={'creationDate'}/>
                </Typography>
            ),
            flex: 1,
            sortable: true,
            renderCell: (params) => (
                <Typography>
                    {params?.row?.created && dayjs(params?.row?.created).format('DD/MM/YYYY')}
                </Typography>
            ),
        },
        ...actionsCol,
    ];

    const handleShowCreateModal = (status: boolean) => () => {
        setShowCreateModal(status);
    };

    const handleShowRequestModal = (status: boolean) => () => {
        setShowRequestModal(status);
    };

    const handleSortValue = (value: GridSortModel) => setSortValue(value[0]);

    const sortedData = useMemo(() => {
        const field = sortValue?.field;
        if (typeof field === 'undefined') {
            return data?.ocppTags;
        }

        const isDataPresent = data && Array.isArray(data.ocppTags) && data.ocppTags.length > 0;

        if (!isDataPresent) {
            return data?.ocppTags;
        }

        return data.ocppTags.slice().sort((a, b) => {
            const af = getObjectFieldAsString(a, field);
            const bf = getObjectFieldAsString(b, field);
            const isValueValid = undefined !== af && undefined !== bf;
            if (isValueValid && sortValue?.sort === 'desc') {
                return af.localeCompare(bf);
            }
            if (isValueValid) {
                return bf?.localeCompare(af);
            }
            return 0;
        });
    }, [sortValue, data?.ocppTags]);

    return (
        // Shaped like every other relationship table on this page - Bexio accounts and custom invoice
        // items - rather than like itself. Empty, it used to swap the whole table for a centred call to
        // action, so the one tab a driver is most likely to open looked like a different product: no
        // column headers, no "no data" where every other tab puts it, and the add row jumping from the
        // foot of the table to the middle of the panel.
        // Flush inside the relationships card: the rows and the add-row footer, no second card.
        <TableLayout
            flush
            hideFooter
            footerPadded={false}
            footer={(canAddRfid || requestsRatherThanCreates) && (
                <Box
                    sx={rfidTableSx.addBox}
                    onClick={
                        requestsRatherThanCreates
                            ? handleShowRequestModal(true)
                            : handleShowCreateModal(true)
                    }
                >
                    <Add sx={{mr: 1, fontSize: '1.25rem'}}/>
                    <Typography sx={rfidTableSx.addText}>
                        {requestsRatherThanCreates
                            ? intl.formatMessage({id: 'requestRfid'})
                            : intl.formatMessage({id: 'addRfid'})}
                    </Typography>
                </Box>
            )}
        >
            <ResponsiveTable
                desktopElement={
                    <Table
                        sx={rfidTableSx.table}
                        rows={sortedData ?? []}
                        hideFooter
                        columns={columns}
                        handleSort={handleSortValue}
                    />
                }
                responsiveElement={
                    <CardList>
                        {data?.ocppTags?.map((item) => (
                            <RfIdItemCard
                                key={item?.id}
                                data={item}
                                businessId={data.business.id}
                            />
                        ))}
                    </CardList>
                }
            />
            <AMPModal
                handleClose={handleShowRequestModal(false)}
                open={showRequestModal}
                title={intl.formatMessage({id: 'requestRfid'})}
                type="md"
                fullWidth
                withoutPaddings
            >
                <RequestRfid closeModal={handleShowRequestModal(false)}/>
            </AMPModal>
            <AMPModal
                handleClose={handleShowCreateModal(false)}
                open={showCreateModal}
                title={intl.formatMessage({id: 'addRfid'})}
                type="md"
                fullWidth
                withoutPaddings
            >
                <CreateRfidForm
                    handleClose={handleShowCreateModal(false)}
                    userData={data}
                    isCompanyUser={isCompanyUser}
                    employeeId={data && 'employeeId' in data ? data.employeeId : undefined}
                    companyId={companyId}
                    onSuccess={onRefetchRfidData}
                />
            </AMPModal>
        </TableLayout>
    );
};